import React, { useState, useEffect, Fragment, useCallback } from 'react';
import { Redirect } from 'react-router-dom';
import Functions from '../../api/Functions';
import { compose } from 'redux';
import { connect } from 'react-redux';
import M from 'materialize-css';
import { firestoreConnect } from 'react-redux-firebase';
import { CSVLink } from "react-csv";
import './ContestsMenu.css';
import { dateToFullDateTime } from '../../utils/DateUtil';

function RunContests(props) {
  const { contests = [], profiles = {}, firestore } = props;
  const [contestId, setContestId] = useState('');
  const [contest, setContest] = useState(null);
  const [contestEntries, setContestEntries] = useState([]);
  const [plainContestEntries, setPlainContestEntries] = useState([]);
  const [winners, setWinners] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => M.AutoInit(), [contests]);

  useEffect(() => {
    if (contestId && Object.keys(profiles).length > 0) {
      const contest = contests.find((c) => c.id === contestId);
      setContest(contest);
    }
  }, [contestId, profiles, contests]);

  const loadContest = useCallback(() => {
    firestore
      .collection('contestEntries')
      .where('idContest', '==', contestId)
      .get()
      .then((snapshot) => {
        const contestEntries = [];
        snapshot.forEach((doc) => contestEntries.push({ id: doc.id, ...doc.data() }));

        const entries = getEntriesByUser(contestEntries, profiles);
        const sortedEntries = getSortedEntries(entries);
        setContestEntries(sortedEntries);


        const plainEntries = getPlainEntries(contestEntries, profiles);
        const sortedPlainEntries = getSortedPlainEntries(plainEntries);
        setPlainContestEntries(sortedPlainEntries);
      })
      .catch((error) => M.toast({ html: error.message }));
  }, [firestore, contestId, profiles]);

  const getSortedEntries = (entries) => {
    const sorted = [];
    for (const key in entries) sorted.push(entries[key]);
    sorted.sort((a, b) => b.counter - a.counter);
    return sorted;
  };

  const getPlainEntries = (contestEntries, profiles) => {
    const entries = [];
    contestEntries.forEach((ce) => {
      const { idUser, day } = ce;
      const profile = profiles[idUser];
      entries.push({ idUser: idUser, day, email: profile.email, name: profile.name, lastName: profile.lastName });
    });
    return entries;
  }

  const getSortedPlainEntries = (entries) => {
    const sorted = [];
    for (const key in entries) sorted.push(entries[key]);
    sorted.sort((a, b) => b.email - a.email);
    return sorted;
  };


  const getEntriesByUser = (contestEntries, profiles) => {
    const entries = {};
    contestEntries.forEach((ce) => {
      const { idUser } = ce;
      const profile = profiles[idUser];
      if (entries[idUser]) entries[idUser].counter++;
      else entries[idUser] = { idUser: idUser, counter: 1, email: profile.email, name: profile.name, lastName: profile.lastName };
    });
    return entries;
  };

  const runContest = () => {
    setLoading(true);
    Functions.runContestById(contestId)
      .then(() => {
        loadContest();
        getWinnerContestsByDate();
      })
      .catch((e) => {
        M.toast({ html: e.message });
        setLoading(false);
      });
  };

  const getWinnerContestsByDate = useCallback(() => {
    firestore
      .collection('winnerContest')
      .where('contestId', '==', contestId)
      .get()
      .then((querySnapshot) => {
        const winnerContests = [];
        querySnapshot.forEach((doc) => winnerContests.push(doc.data()));
        const winners = winnerContests.map((w) => {
          const { userId, createdAt } = w;
          const profile = profiles[userId];
          return {
            id: w.id,
            idUser: userId,
            email: profile.email,
            name: profile.name,
            lastName: profile.lastName,
            createdAt: dateToFullDateTime(createdAt.toDate())
          };
        });
        setWinners(winners);
      })
      .catch((e) => {

        console.log(e);
        console.log(e.message);

        M.toast(
          {
            html: e.message
          }
        )
      })
      .finally(() => setLoading(false));
  }, [contestId, firestore, profiles]);

  useEffect(() => {
    if (contest) {
      loadContest();
      getWinnerContestsByDate();
    }
  }, [contest, loadContest, getWinnerContestsByDate]);

  const sendNotificationToWinners = () => {
    setLoading(true);
    Functions.sendNotificationToWinners(contestId)
      .then(() => M.toast({ html: 'Notification sent to winners' }))
      .catch((e) => M.toast({ html: e.message }))
      .finally(() => setLoading(false));
  };

  const finishContest = () => {
    setLoading(true);
    Functions.finishContest(contestId)
      .then(() => {
        getContestById();
        M.toast({ html: 'Contest updated' });
      })
      .catch((e) => M.toast({ html: e.message }))
      .finally(() => setLoading(false));
  };

  const getContestById = () => {
    firestore
      .collection('contests')
      .doc(contestId)
      .get()
      .then((doc) => setContest(doc.data()))
      .catch((e) => M.toast({ html: e.message }));
  };

  const deleteWinnerContest = (id) => {
    firestore
      .collection('winnerContest')
      .doc(id)
      .delete()
      .then(() => {
        M.toast({ html: 'Winner removed' });
        getWinnerContestsByDate();
      })
      .catch((error) => M.toast({ html: error.message }));
  };

  const deleteContestEntriesByContest = (contestId) => {
    setLoading(true);
    Functions.deleteContestEntriesByContest(contestId)
      .then(() => {

        M.toast({ html: 'Entries deleted' });
      })
      .catch((e) => {
        console.log(e);
        console.log(e.message);
        M.toast({ html: e.message })
      })
      .finally(() => setLoading(false))
  }

  const renderContestsSelect = () => (
    <Fragment>
      <select
        value={contestId}
        onChange={(e) => {
          setWinners([]);
          setContestId(e.target.value);
        }}>
        <option value="" disabled>
          Choose a contest
        </option>
        {contests.map((contest) => renderContestOption(contest))}
      </select>
      <label>Contest</label>
    </Fragment>
  );

  const renderContestOption = (contest) => (
    <option key={contest.id} value={contest.id}>
      {`${contest.name} (${contest.countryCode})`}
    </option>
  );

  const renderActions = () => (
    <Fragment>
      <div className="row" style={{ marginRight: 5 }}>
        {`1. `}
        {renderInstructionButton('Run contest', runContest, !contest || contest.wasAnalyzed || (contest && contestEntries.length === 0))}
        {` as many times as you need (Each time generates a new winner)`}
      </div>
      <div className="row">2. Confirm the list of winners. Remove / Add winners (run contest again)</div>
      <div className="row">
        {`3. `}
        {renderInstructionButton('Notify all winners', sendNotificationToWinners, !contest || contest.wasAnalyzed || winners.length === 0)}
      </div>
      <div className="row">
        {`4. `}
        {renderInstructionButton('Finish contest', finishContest, !contest || contest.wasAnalyzed)}
        {` (invalidates remaining entries and moves dates for next contest period)`}
      </div>
    </Fragment>
  );

  const renderInstructionButton = (label, action, disabled) => (
    <button disabled={disabled} className="waves-effect waves-light btn-small" onClick={() => action()}>
      {label}
    </button>
  );

  const renderContestDetails = () =>
    contest && (
      <div className="row" style={{ backgroundColor: '#f8f8f8', marginLeft: 5, padding: 10, alignSelf: 'center' }}>
        {!contest.active && <div style={{ textAlign: 'center', backgroundColor: '#f8f8f8', width: '100%', marginBottom: 15, padding: 10, fontWeight: 'bold' }}>Contest not active</div>}
        {renderContestDates()}
      </div>
    );

  const renderContestDates = () => (
    <div className="row">
      <div className="col s6">
        <div>Contest date:</div>
        <div>Valid until:</div>
        <div>Start date:</div>
        <div>End date:</div>
        <div>Formatted start date:</div>
        <div>Formatted end date:</div>
      </div>
      <div className="col s6">
        <div>{contest.contestDate ? dateToFullDateTime(contest.contestDate.toDate()) : ''}</div>
        <div>{contest.validDate ? dateToFullDateTime(contest.validDate.toDate()) : ''}</div>
        <div>{contest.startDate ? dateToFullDateTime(contest.startDate.toDate()) : ''}</div>
        <div>{contest.endDate ? dateToFullDateTime(contest.endDate.toDate()) : ''}</div>
        <div>{contest.formattedStartDate}</div>
        <div>{contest.formattedEndDate}</div>
      </div>
    </div>
  );

  const renderLoading = () =>
    loading && (
      <div style={{ textAlign: 'center' }}>
        <div className="preloader-wrapper big active">
          <div className="spinner-layer spinner-green-only">
            <div className="circle-clipper left">
              <div className="circle"></div>
            </div>
            <div className="gap-patch">
              <div className="circle"></div>
            </div>
            <div className="circle-clipper right">
              <div className="circle"></div>
            </div>
          </div>
        </div>
      </div>
    );

  const renderParticipantsWithEntries = () =>
    contestId ? (
      contestEntries.length > 0 ? (
        <div>
          <br /><br />
          <div>
            <CSVLink
              filename={contest.name}
              className='waves-effect waves-light btn-small'
              target="_blank"
              data={contestEntries}>Export Participants</CSVLink>
            <br /><br />
            <CSVLink
              filename={contest.name}
              className='waves-effect waves-light btn-small'
              target="_blank"
              data={plainContestEntries}>Export Entries</CSVLink>
            <br /><br />
          </div>
          <div className="row">
            <h5 className="col s6">Participants</h5>
            <h5 className="col s6" style={{ textAlign: 'end' }}>
              Entries
            </h5>
          </div>
          <div className="">{contestEntries.map((c) => renderParticipant(c))}</div>
        </div>
      ) : (
        <div style={{ fontWeight: 'bold' }}>No entries registered for this contest</div>
      )
    ) : null;

  const renderParticipant = (user) => (
    <div className="row" key={user.idUser} style={{ backgroundColor: '#f8f8f8', padding: 10, marginBottom: 5 }}>
      <div className="col s6">
        <div style={{ color: '#26A59A' }}>{user.idUser}</div>
        <div>{`${user.name} ${user.lastName}`}</div>
        <div>{user.email}</div>
      </div>
      <div className="col s6" style={{ textAlign: 'end' }}>
        {user.counter}
      </div>
    </div>
  );

  const renderWinners = () =>
    winners.length > 0 ? (
      <div>
        <div className="row">
          <h5>Winners</h5>
        </div>
        <div className="scrollContainer">{winners.map((winner) => renderWinner(winner))}</div>
      </div>
    ) : null;

  const renderWinner = (user) => (
    <div className="row" key={user.idUser} style={{ backgroundColor: '#f8f8f8', padding: 10, marginBottom: 5 }}>
      <div className="col s8">
        <div style={{ color: '#26A59A' }}>{`${user.name} ${user.lastName}`}</div>
        <div>{user.idUser}</div>
        <div>{user.email}</div>
        <div>{user.createdAt}</div>
      </div>
      <div className="col s4" style={{ textAlign: 'center' }}>
        <button className="waves-effect waves-light btn-small" onClick={() => deleteWinnerContest(user.id)}>
          Remove
        </button>
      </div>
    </div>
  );

  const { auth } = props;
  if (!auth.uid) return <Redirect to="/signin" />;
  return (
    <div className="container" style={{ marginBottom: 50 }}>
      <h4>Run Contests</h4>
      {renderContestsSelect()}
      <div>
        <button className="waves-effect waves-light btn-small" onClick={() => deleteContestEntriesByContest(contestId)}>
          Delete all contest Entries
        </button>
        <br /><br /><br /><br />
      </div>
      <div className="row">
        <div className="col s7">{renderActions()}</div>
        <div className="col s5">{renderContestDetails()}</div>
      </div>
      {renderLoading()}
      <div className="row">
        <div className="col s6">{renderParticipantsWithEntries()}</div>
        <div className="col s6">{renderWinners()}</div>
      </div>
    </div>
  );
}

const mapStateToProps = (state) => {
  return {
    auth: state.firebase.auth,
    contests: state.firestore.ordered.contests,
    profiles: state.firestore.data.profiles,
  };
};

export default compose(firestoreConnect([
  {
    collection: 'contests',
    orderBy: ['name', 'asc']
  },
  {
    collection: 'profiles'
  }
]), connect(mapStateToProps))(RunContests);
